/**
 * PlanChangeHistory Component - Sprint 25
 * 
 * Displays plan change history in a table format:
 * - Event Type
 * - Old Plan → New Plan  
 * - Timestamp
 * - Triggered By
 * - Notes
 * 
 * Consumes: GET /api/billing/tenant/{id}/events
 */

import React, { useState, useEffect } from 'react';
import { 
  History, 
  ArrowRight, 
  ArrowUp, 
  ArrowDown,
  AlertCircle,
  RefreshCw,
  FileText,
  Shield,
  UserX
} from 'lucide-react';
import { api } from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// Event type configuration
const EVENT_CONFIG = {
  plan_change: {
    label: 'Plan Change',
    icon: ArrowRight,
    color: 'text-blue-600 bg-blue-50'
  },
  upgrade: {
    label: 'Upgrade',
    icon: ArrowUp,
    color: 'text-emerald-600 bg-emerald-50'
  },
  downgrade: {
    label: 'Downgrade',
    icon: ArrowDown,
    color: 'text-amber-600 bg-amber-50'
  },
  billing_status_change: {
    label: 'Status Change',
    icon: FileText,
    color: 'text-purple-600 bg-purple-50'
  },
  downgrade_blocked: {
    label: 'Downgrade Blocked',
    icon: AlertCircle,
    color: 'text-red-600 bg-red-50'
  },
  seat_limit_blocked: {
    label: 'Seat Limit Blocked',
    icon: UserX,
    color: 'text-red-600 bg-red-50'
  },
  plan_changed: {
    label: 'Plan Changed',
    icon: ArrowRight,
    color: 'text-blue-600 bg-blue-50'
  },
  tier_migration: {
    label: 'Tier Migration',
    icon: Shield,
    color: 'text-indigo-600 bg-indigo-50'
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPlanName = (tier) => {
  if (!tier) return '—';
  return tier.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const PlanChangeHistory = ({ tenantId, limit = 10 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEvents = async () => {
    if (!tenantId || tenantId === 'default-tenant') {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/billing/tenant/${tenantId}/events?limit=${limit}`);
      setEvents(response.data.events || []);
    } catch (err) {
      console.error('Failed to load billing events:', err);
      setError('Failed to load plan history');
      toast.error('Failed to load plan history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [tenantId, limit]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32 text-gray-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="plan-change-history">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-gray-600" />
            Plan Change History
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadEvents}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No plan changes recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className="text-left py-2 px-3 font-medium">Event</th>
                  <th className="text-left py-2 px-3 font-medium">Change</th>
                  <th className="text-left py-2 px-3 font-medium">Date</th>
                  <th className="text-left py-2 px-3 font-medium">By</th>
                  <th className="text-left py-2 px-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, idx) => {
                  const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.plan_change;
                  const IconComponent = config.icon;
                  
                  // Get old and new plan from various possible fields
                  const oldPlan = event.old_plan || event.from_tier || event.old_status || '—';
                  const newPlan = event.new_plan || event.to_tier || event.new_status || '—';
                  const timestamp = event.timestamp || event.created_at;
                  
                  return (
                    <tr 
                      key={event.id || event.event_id || idx} 
                      className="border-b last:border-0 hover:bg-gray-50"
                    >
                      <td className="py-3 px-3">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${config.color}`}>
                          <IconComponent className="w-3.5 h-3.5" />
                          <span className="font-medium text-xs">{config.label}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">{formatPlanName(oldPlan)}</span>
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{formatPlanName(newPlan)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                        {formatDate(timestamp)}
                      </td>
                      <td className="py-3 px-3">
                        <Badge variant="outline" className="text-xs capitalize">
                          {event.triggered_by || 'system'}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-gray-600 max-w-xs truncate">
                        {event.notes || event.details?.reason || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlanChangeHistory;
